import { Button } from '@1trading/react';
import { ArrowLeft, CircleAlert } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { StatusChip } from '@/components/ui/status-chip';
import { batchStatusVariant } from '@/features/brokers/batches/batch-status';
import { BatchSummaryStrip } from '@/features/brokers/batches/batch-summary-strip';
import { BatchTransactionsSection } from '@/features/brokers/batches/batch-transactions-section';
import { useBrokerBatch, useBrokerBatchTransactions } from '@/hooks/queries/brokers/use-broker-batch';
import { useQueryErrorToast } from '@/hooks/use-query-error-toast';
import { getErrorMessage, isNotFoundError } from '@/lib/api/error';

const BrokerBatchPage = () => {
  const navigate = useNavigate();
  // React Router decodes the params; the service re-encodes ONCE when composing
  // bop-api paths — never encode here on top.
  const { brokerId = '', batchId = '' } = useParams();

  // The query string is what restores the Transactions tab + Batches segment on
  // the detail page — this page is the only producer of `?txn=`.
  const backToBatchesUrl = `/operations/brokers/${brokerId}?tab=transactions&txn=batches`;

  // Both queries mount together → summary and first transactions page load in
  // parallel with independent loading/error flags (legacy Promise.all).
  const batchQuery = useBrokerBatch(brokerId, batchId);
  const transactionsQuery = useBrokerBatchTransactions(brokerId, batchId);

  const isBatchNotFound = batchQuery.isError && isNotFoundError(batchQuery.error);

  useEffect(() => {
    // A definitive 404 renders the not-found panel instead of toasting
    if (batchQuery.isError && !isNotFoundError(batchQuery.error)) {
      toast.error(`Failed to fetch transaction batch: ${getErrorMessage(batchQuery.error)}`);
    }
  }, [batchQuery.isError, batchQuery.error]);

  useQueryErrorToast(transactionsQuery, `Failed to fetch batch transactions: ${getErrorMessage(transactionsQuery.error)}`);

  const batch = batchQuery.data;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <header className="flex flex-col gap-2">
        <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to={backToBatchesUrl}>
          <ArrowLeft className="size-4" />
          Back to batches
        </Link>
        {/* Title from the ROUTE PARAM (decoded), not the response — it renders even
            while the summary loads or fails */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="break-all font-mono text-[22px] font-semibold">{batchId}</h1>
          {batch ? (
            <StatusChip className="px-2 py-0.5 text-xs" variant={batchStatusVariant(batch.status)}>
              {batch.status}
            </StatusChip>
          ) : null}
        </div>
      </header>

      {isBatchNotFound ? (
        // A wrong or stale URL must not look like an empty batch
        <EmptyState
          action={
            <Button onClick={() => navigate(backToBatchesUrl)} type="button" variant="outline">
              Back to Batches
            </Button>
          }
          icon={CircleAlert}
          title="Batch not found."
        />
      ) : batchQuery.isPending ? (
        // Full-page loading panel only while the summary loads with nothing held
        <LoadingPanel className="py-24" />
      ) : (
        <>
          {batchQuery.isError ? (
            // Legacy behaviour preserved: a failed summary never blocks
            // the transactions below — surfaced inline with Retry instead
            <QueryErrorBanner error={batchQuery.error} onRetry={() => batchQuery.refetch()} title="Failed to fetch transaction batch" />
          ) : null}
          {batch ? <BatchSummaryStrip batch={batch} /> : null}
          <BatchTransactionsSection query={transactionsQuery} />
        </>
      )}
    </div>
  );
};
BrokerBatchPage.displayName = 'BrokerBatchPage';

export default BrokerBatchPage;
